<template>
  <form @submit.prevent="handleSubmit">
    <!-- O combo de uma linha de preço não muda: na edição ele é só o título. -->
    <div v-if="entry" class="form-field" data-testid="so-combo-prices-combo-fixo">
      <span class="combo-rotulo">{{ t('Combo') }}</span>
      <strong>{{ entry.so_combo_name }}</strong>
    </div>
    <div v-else class="form-field">
      <select-list
        elem-id="soComboPriceComboId"
        data-testid="so-combo-prices-combo-select"
        v-model="formData.so_combo_price_combo_id"
        :itens-select="comboOptions"
        :label="t('Combo')"
        :placeholder="t('Selecione o combo')"
        :auto-select-first="false"
      />
      <small v-if="errors.so_combo_price_combo_id" class="error-message">{{ errors.so_combo_price_combo_id }}</small>
    </div>

    <div class="form-field">
      <input-currency
        elem-id="soComboPricePrice"
        data-testid="so-combo-prices-price-input"
        v-model="formData.so_combo_price_price"
        :currency="moeda ?? undefined"
        :label="t('Preço na unidade')"
      />
      <small v-if="errors.so_combo_price_price" class="error-message">{{ errors.so_combo_price_price }}</small>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, reactive, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCombos } from '../../composables/useSOCombos';
import { useSOComboPrices } from '../../composables/useSOComboPrices';
import { moedaDaFranquia } from '../../composables/useMoedaFranquia';
import { emitValidationFailed } from '../../composables/useFormValidation';
import { eventBus } from '../../eventBus';
import { error as logError } from '../../utils/DebugLogger';
import type { ComboPrices_PriceRow } from '../../contracts/ComboPrices_PriceRow';

const props = withDefaults(defineProps<{
  franchiseId: number | string | null;
  entry?: ComboPrices_PriceRow | null;
}>(), { entry: null });

const emit = defineEmits<{
  'submit-success': [];
}>();

const { t } = useI18n();
const { fetchAll: fetchCombos } = useSOCombos();
const { fetchByFranchise, save } = useSOComboPrices();

const moeda = computed(() => moedaDaFranquia(props.franchiseId));

const formData = reactive({
  so_combo_price_combo_id: props.entry?.so_combo_price_combo_id as number | string | undefined,
  // O wire da listagem fala UNIDADES: o form edita o que a tela mostra.
  so_combo_price_price: props.entry?.so_combo_price_price,
});

const errors = reactive({
  so_combo_price_combo_id: '',
  so_combo_price_price: '',
});

const comboOptions = ref<{ ID: number | string; Name: string }[]>([]);

/**
 * Só na criação: os combos ativos na rede que a unidade ainda não precificou.
 * O que ela já tem se edita na tabela.
 */
const loadOptions = async (): Promise<void> => {
  if (props.entry) return;
  try {
    const [combos, jaPrecificados] = await Promise.all([fetchCombos(), fetchByFranchise(Number(props.franchiseId))]);
    const usados = new Set(jaPrecificados.map((p) => p.so_combo_price_combo_id));
    comboOptions.value = combos
      .filter((c) => Number(c.so_combo_active) === 1 && !usados.has(c.so_combo_id))
      .map((c) => ({ ID: c.so_combo_id, Name: c.so_combo_name ?? '' }));
  } catch (error: unknown) {
    logError('SOComboPricesForm', 'loadOptions', 'Error loading options', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao carregar opcoes') + ': ' + message);
  }
};

const validateForm = (): boolean => {
  errors.so_combo_price_combo_id = formData.so_combo_price_combo_id ? '' : t('Campo obrigatório');
  errors.so_combo_price_price = formData.so_combo_price_price && formData.so_combo_price_price > 0
    ? ''
    : t('Informe um preço maior que zero');

  const isValid = !errors.so_combo_price_combo_id && !errors.so_combo_price_price;
  if (!isValid) {
    emitValidationFailed('SOComboPricesForm', errors);
  }
  return isValid;
};

const handleSubmit = async (): Promise<void> => {
  if (!validateForm()) return;

  try {
    await save({
      so_combo_price_combo_id: Number(formData.so_combo_price_combo_id),
      // UNIDADES na tela -> CENTAVOS no wire de salvar (o mesmo da matriz).
      so_combo_price_price: Math.round((formData.so_combo_price_price ?? 0) * 100),
      ...(props.entry
        ? { so_combo_price_id: props.entry.so_combo_price_id }
        : { so_combo_price_franchise_id: Number(props.franchiseId) }),
    });
    eventBus.emit('globalMessage.success', props.entry ? t('Preço do combo atualizado') : t('Preço do combo criado'));
    emit('submit-success');
  } catch (error: unknown) {
    logError('SOComboPricesForm', 'handleSubmit', 'Error submitting form', error);
    const message = error instanceof Error ? error.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar o preço do combo') + ': ' + message);
  }
};

onMounted(loadOptions);

defineExpose({ handleSubmit, validateForm });
</script>

<style scoped>
.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.combo-rotulo {
  font-size: 0.875rem;
  color: var(--p-text-muted-color);
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
